import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  ChartColumn,
  Copy,
  ExternalLink,
  Eye,
  Link2,
  Palette,
  Pencil,
  QrCode,
  Share2,
  Star,
} from "lucide-react";
import { useState, useSyncExternalStore } from "react";
import { Link, useNavigate, useOutletContext } from "react-router";
import { api } from "../api/client";
import { menuApi, openPreview, useDraft, useImports, useMenus } from "../api/queries";
import { FlowSteps } from "../components/FlowSteps";
import { Switch } from "../components/kit";
import { useToast } from "../components/Toast";
import { Alert, ICON } from "../components/ui";
import { formatWhen, initialsOf, plural } from "../lib/format";
import type { ShellContext } from "./AppShell";
import { LaunchChecklist, useLaunch } from "./LaunchChecklist";
import { mainMenu } from "./menus/status";
import { useRefreshMenus } from "./menus/useRefresh";
import { nextStep } from "./nextStep";
import { useAllergenDeadlines } from "./nutrition/AllergenBanner";

// Acasă in direction A „Noapte” (owner 2026-11-04, DEC-130): the next step with the launch progress ring, the last
// 30 days in numbers, the launch list, today's availability and the latest reviews — and, beside the work, the
// restaurant's own menu on a phone (the member-only draft preview). Real data only, no demo figures.

interface Stats {
  live: boolean;
  days: { day: string; views: number; qr: number }[];
  period: { views: number; qr: number };
  previous: { views: number } | null;
}
interface Feedback {
  id: string;
  rating: number;
  message: string;
  createdAt: number;
}

const todayIso = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Bucharest", dateStyle: "short" }).format(new Date());
const daysBefore = (s: string, n: number) =>
  new Date(Date.parse(`${s}T00:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);
const nInt = (n: number) => n.toLocaleString("ro-RO");

const WIDE = "(min-width: 1180px)";
function useWide() {
  return useSyncExternalStore(
    (cb) => {
      const m = window.matchMedia(WIDE);
      m.addEventListener("change", cb);
      return () => m.removeEventListener("change", cb);
    },
    () => window.matchMedia(WIDE).matches,
    () => true,
  );
}

/** A small line of the daily openings (decorative; the numbers are in the card). */
function Spark({ values }: { values: number[] }) {
  if (values.length < 2) return null;
  const max = Math.max(1, ...values);
  const pts = values.map(
    (v, i) => `${((i / (values.length - 1)) * 200).toFixed(1)},${(34 - (v / max) * 30).toFixed(1)}`,
  );
  const line = `M${pts.join(" L")}`;
  return (
    <svg className="ov-spark" viewBox="0 0 200 38" preserveAspectRatio="none" aria-hidden="true">
      <path d={`${line} L200,38 L0,38 Z`} fill="var(--color-primary)" opacity="0.07" />
      <path
        d={line}
        fill="none"
        stroke="var(--color-primary)"
        strokeWidth="2"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export function OverviewPage() {
  const { me, membership } = useOutletContext<ShellContext>();
  const r = membership.restaurant;
  const menus = useMenus(r.id);
  const main = mainMenu(menus.data ?? []);
  const draft = useDraft(r.id, main?.id ?? null);
  const navigate = useNavigate();
  const toast = useToast();
  const refresh = useRefreshMenus(r.id);
  const first = me.user.name.split(" ")[0];
  const [copied, setCopied] = useState(false);
  const imports = useImports(r.id);
  const deadlines = useAllergenDeadlines(r.id);
  const launch = useLaunch(r.id, menus.data ?? []);
  const wide = useWide();
  const to = todayIso();
  const stats = useQuery({
    queryKey: ["stats", r.id, daysBefore(to, 29), to],
    enabled: !!main,
    queryFn: () => api<Stats>("GET", `/restaurants/${r.id}/stats?from=${daysBefore(to, 29)}&to=${to}`),
  });
  const feedback = useQuery({
    queryKey: ["feedback", r.id],
    enabled: !!main,
    queryFn: () => api<{ feedback: Feedback[] }>("GET", `/restaurants/${r.id}/feedback`),
  });
  const step = nextStep({
    menus: menus.data ?? [],
    main,
    latestImport: imports.data?.[0],
    allergenDeadlines: deadlines.data?.length ?? 0,
    publicUrl: r.publicUrl,
  });

  if (menus.isLoading) {
    return (
      <p className="muted" role="status">
        Se încarcă…
      </p>
    );
  }
  if (menus.isError) {
    return (
      <Alert tone="error" live>
        {menus.error.message}
      </Alert>
    );
  }

  const ring = (
    <div
      className="ov-ring"
      role="img"
      aria-label={`Lansare: ${launch.doneCount} din ${launch.items.length} pași gata`}
      style={{
        background: `conic-gradient(var(--color-accent) ${(launch.doneCount / launch.items.length) * 100}%, rgba(42, 27, 77, 0.12) 0)`,
      }}
    >
      <div>
        <b>
          {launch.doneCount}/{launch.items.length}
        </b>
        <small>gata</small>
      </div>
    </div>
  );
  const stepCard = step && (
    <section className="ov-next" aria-labelledby="ov-next">
      {launch.ready && ring}
      <div className="ov-next-body">
        <span className="ov-eyebrow">Următorul pas</span>
        <h2 id="ov-next">{step.title}</h2>
        <p>{step.body}</p>
        <div className="ov-acts">
          {step.external ? (
            <a className="btn btn-primary" href={step.to} target="_blank" rel="noopener">
              <ExternalLink {...ICON} aria-hidden="true" />
              {step.action}
            </a>
          ) : (
            <Link className="btn btn-primary" to={step.to}>
              {step.action}
              <ArrowRight size={18} strokeWidth={1.75} aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );

  if (!main) {
    if (step) {
      return (
        <>
          <div className="stack" style={{ gap: 6 }}>
            <span className="greet">Bun venit, {first}</span>
            <h1 className="h1">{r.name}</h1>
          </div>
          {stepCard}
        </>
      );
    }
    return (
      <>
        <div className="stack" style={{ gap: 6 }}>
          <span className="greet">Bun venit, {first}</span>
          <h1 className="h1">Hai să punem meniul {r.name} online</h1>
          <span className="page-sub" style={{ fontSize: 15, lineHeight: "22px", maxWidth: 640 }}>
            Trei pași. Nimic nu devine public până nu apeși „Publică meniul”. Aspectul îl alegi oricând după.
          </span>
        </div>
        <FlowSteps current={0} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
            gap: 16,
          }}
        >
          <div
            className="stack"
            style={{
              background: "var(--color-selected)",
              border: "1.5px dashed var(--color-selected-line)",
              borderRadius: 20,
              padding: 28,
              gap: 12,
            }}
          >
            <span className="badge-accent">Recomandat</span>
            <span className="h2">Încarcă meniul existent</span>
            <span className="muted" style={{ fontSize: 15, lineHeight: "22px" }}>
              Încarci un PDF sau fotografii ale meniului. Pregătim categoriile, produsele și prețurile; tu
              verifici doar ce e marcat și publici.
            </span>
            <button
              type="button"
              className="btn btn-primary"
              style={{ alignSelf: "flex-start" }}
              onClick={() => navigate("/app/imports")}
            >
              Încarcă meniul
            </button>
          </div>
          <div className="card stack" style={{ gap: 12, padding: 28 }}>
            <span className="h2">Creează meniul manual</span>
            <span className="muted" style={{ fontSize: 15, lineHeight: "22px" }}>
              Pornești de la un meniu gol și adaugi categoriile și produsele unul câte unul. Potrivit pentru
              meniuri scurte.
            </span>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ alignSelf: "flex-start" }}
              onClick={async () => {
                const m = await menuApi(r.id).create("Meniul principal");
                await refresh(m.id);
                toast("Meniul principal a fost creat ca ciornă. Adaugă prima categorie.");
                navigate(`/app/products?menu=${m.id}`);
              }}
            >
              Creează meniul
            </button>
          </div>
        </div>
        <Alert tone="info">
          Linkul tău public va fi{" "}
          <strong style={{ fontWeight: 600 }}>{r.publicUrl.replace(/^https?:\/\//, "")}</strong>. Codul QR
          apare după prima publicare și nu se schimbă la actualizări.
        </Alert>
      </>
    );
  }

  const d = draft.data;
  const quick = (d?.products ?? []).slice(0, 5);
  const pending = main.pendingChanges ?? 0;
  const host = r.publicUrl.replace(/^https?:\/\//, "");
  const preview = `/api/restaurants/${r.id}/preview?menu=${main.id}&embed=1`;
  const s = stats.data;
  const delta =
    s?.previous && s.previous.views > 0
      ? Math.round(((s.period.views - s.previous.views) / s.previous.views) * 100)
      : null;
  const reviews = feedback.data?.feedback ?? [];
  const avg = reviews.length ? reviews.reduce((a, f) => a + f.rating, 0) / reviews.length : null;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(r.publicUrl);
      setCopied(true);
      toast(`Link copiat: ${host}`);
    } catch {
      setCopied(false);
    }
  }
  async function share() {
    if (navigator.share) {
      try {
        await navigator.share({ title: r.name, url: r.publicUrl });
        return;
      } catch {
        // Closed by the user: nothing to do.
        return;
      }
    }
    await copyLink();
  }
  const openMenu = () =>
    main.publicVisible ? window.open(r.publicUrl, "_blank", "noopener") : openPreview(r.id, main.id);

  const allDone = launch.ready && launch.doneCount === launch.items.length;

  return (
    <>
      <div className="ov-hello">
        <div>
          <span className="greet">Bună ziua, {first}</span>
          <h1 className="h1">{r.name}</h1>
        </div>
        <div className="ov-status">
          {main.publicVisible ? (
            <span className="ov-pill live">
              <span className="ov-dot" />
              Publicat
            </span>
          ) : (
            <span className="ov-pill off">
              <span className="ov-dot" />
              Nepublicat
            </span>
          )}
          {pending > 0 && (
            <Link className="ov-pill" to={`/app/products?menu=${main.id}&publish=1`}>
              <Pencil size={14} strokeWidth={1.75} aria-hidden="true" />
              {plural(pending, "modificare în ciornă", "modificări în ciornă")}
            </Link>
          )}
        </div>
      </div>

      <div className="ov-cols">
        <div className="ov-main">
          {stepCard ??
            (allDone && (
              <section className="ov-next" aria-labelledby="ov-next">
                {ring}
                <div className="ov-next-body">
                  <span className="ov-eyebrow">Totul e gata</span>
                  <h2 id="ov-next">Meniul tău e complet și online</h2>
                  <p>
                    Schimbările de prețuri sau produse le faci din Produse; clienții le văd după publicare.
                  </p>
                </div>
              </section>
            ))}

          {!wide && (
            <section className="card ov-mini" aria-label="Meniul tău">
              <div className="ov-mini-phone">
                <iframe
                  title="Meniul tău, în miniatură"
                  src={preview}
                  tabIndex={-1}
                  aria-hidden="true"
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
              <div style={{ minWidth: 0 }}>
                <b>{main.publicVisible ? "Meniul tău e online" : "Meniul tău, în ciornă"}</b>
                <p>{host}</p>
                <div className="ov-acts">
                  <button type="button" className="btn btn-secondary" onClick={openMenu}>
                    <Eye size={16} strokeWidth={1.75} aria-hidden="true" />
                    Deschide
                  </button>
                  {main.publicVisible && (
                    <button type="button" className="btn btn-secondary" onClick={share}>
                      <Share2 size={16} strokeWidth={1.75} aria-hidden="true" />
                      Trimite
                    </button>
                  )}
                </div>
              </div>
            </section>
          )}

          <div className="ov-kpis">
            <Link className="card ov-kpi" to="/app/analytics">
              <span className="ov-kpi-label">
                <Eye size={16} strokeWidth={1.75} aria-hidden="true" />
                Deschideri ale meniului
              </span>
              <span className="ov-kpi-value">
                {s ? nInt(s.period.views) : "—"} <small>în 30 de zile</small>
              </span>
              <span className={`ov-kpi-note${delta !== null && delta > 0 ? " up" : ""}`}>
                {delta === null
                  ? "Graficul complet în Statistici"
                  : `${delta > 0 ? "+" : ""}${delta}% față de cele 30 de zile dinainte`}
              </span>
              {s && <Spark values={s.days.map((x) => x.views)} />}
            </Link>
            <Link className="card ov-kpi" to="/app/qr">
              <span className="ov-kpi-label">
                <QrCode size={16} strokeWidth={1.75} aria-hidden="true" />
                Scanări QR
              </span>
              <span className="ov-kpi-value">{s ? nInt(s.period.qr) : "—"}</span>
              <span className="ov-kpi-note">
                {s && s.period.views > 0
                  ? `${Math.round((s.period.qr / s.period.views) * 100)}% din deschideri`
                  : "Din codurile de pe mese"}
              </span>
            </Link>
            <Link className="card ov-kpi" to="/app/feedback">
              <span className="ov-kpi-label">
                <Star size={16} strokeWidth={1.75} aria-hidden="true" />
                Nota clienților
              </span>
              <span className="ov-kpi-value">
                {avg === null ? "—" : avg.toLocaleString("ro-RO", { maximumFractionDigits: 1 })}{" "}
                {avg !== null && <small>/ 5</small>}
              </span>
              <span className="ov-kpi-note">
                {avg === null ? "Nicio recenzie încă" : plural(reviews.length, "recenzie", "recenzii")}
              </span>
            </Link>
          </div>

          <LaunchChecklist launch={launch} />

          <div className="ov-two">
            {quick.length > 0 && (
              <section className="card" aria-labelledby="ov-av">
                <div className="ov-panel-head">
                  <h2 id="ov-av">Disponibil azi</h2>
                  <Link className="btn-link" to={`/app/products?menu=${main.id}`}>
                    Toate ({d?.products.length ?? 0})
                  </Link>
                </div>
                {quick.map((p, i) => (
                  <div className="ov-row" key={p.id}>
                    <span className={`ov-mono mono-${(i % 5) + 1}`} aria-hidden="true">
                      {initialsOf(p.name)}
                    </span>
                    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
                      <span
                        style={{
                          fontWeight: 600,
                          color: p.available ? "var(--color-text)" : "var(--color-text-muted)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.name}
                      </span>
                      <span
                        className="caption"
                        style={{ color: p.available ? "var(--color-success)" : "var(--color-error)" }}
                      >
                        {p.available ? "Disponibil" : "Indisponibil azi"}
                      </span>
                    </div>
                    <Switch
                      checked={p.available}
                      label={`${p.name}: disponibil`}
                      onChange={async () => {
                        const api = menuApi(r.id);
                        const res = await api.setAvailability(p.id, !p.available);
                        await refresh(main.id);
                        toast(
                          `${p.name}: ${res.available ? "disponibil din nou" : "indisponibil azi"}. Apare imediat în meniul public.`,
                          async () => {
                            await api.setAvailability(p.id, res.previous);
                            await refresh(main.id);
                          },
                        );
                      }}
                    />
                  </div>
                ))}
              </section>
            )}
            <section className="card" aria-labelledby="ov-fb">
              <div className="ov-panel-head">
                <h2 id="ov-fb">Recenzii recente</h2>
                <Link className="btn-link" to="/app/feedback">
                  Toate
                </Link>
              </div>
              {reviews.length === 0 ? (
                <p className="caption muted" style={{ margin: 0 }}>
                  Încă nicio recenzie. Clienții o lasă din meniu, cu butonul „Scrie o recenzie”.
                </p>
              ) : (
                reviews.slice(0, 3).map((f) => (
                  <div className="ov-row" key={f.id} style={{ alignItems: "flex-start", padding: "10px 0" }}>
                    <span className="ov-mono mono-4" aria-hidden="true">
                      <Star size={16} strokeWidth={1.75} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600 }}>
                        {plural(f.rating, "stea", "stele")}
                        <span className="caption muted" style={{ fontWeight: 400 }}>
                          {" "}
                          · {formatWhen(f.createdAt)}
                        </span>
                      </span>
                      {f.message && (
                        <span
                          className="caption muted"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          „{f.message}”
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </section>
          </div>
        </div>

        {wide && (
          <aside className="ov-live" aria-label="Meniul tău">
            <div className="ov-live-head">
              <b>{pending > 0 ? "Ciorna ta, pe telefon" : "Meniul tău, acum"}</b>
              {main.publicVisible && (
                <span className="ov-pill live">
                  <span className="ov-dot" />
                  Live
                </span>
              )}
            </div>
            <div className="ov-phone">
              <div className="ov-phone-screen">
                <iframe
                  title="Previzualizarea meniului pe telefon"
                  src={preview}
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
            </div>
            <div className="ov-url">
              <Link2 size={16} strokeWidth={1.75} aria-hidden="true" />
              <span>{host}</span>
              <button type="button" className="btn btn-secondary" onClick={copyLink}>
                <Copy size={16} strokeWidth={1.75} aria-hidden="true" />
                {copied ? "Copiat" : "Copiază"}
              </button>
            </div>
            <div className="ov-live-acts">
              <Link className="btn btn-secondary" to="/app/appearance">
                <Palette size={16} strokeWidth={1.75} aria-hidden="true" />
                Aspect
              </Link>
              <Link className="btn btn-secondary" to="/app/analytics">
                <ChartColumn size={16} strokeWidth={1.75} aria-hidden="true" />
                Statistici
              </Link>
            </div>
          </aside>
        )}
      </div>
    </>
  );
}
